import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Button,
  Grid,
  Box,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useCheckout } from "./CheckoutContext";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

const getColorFromTitle = (title) => {
  if (title.includes("Black White")) return "Black";
  if (title.includes("Tonal Cream")) return "Cream";
  return "Default Color";
};

const CartDrawerContent = ({ closeDrawer, userCountry }) => {
  const {
    state: { cartItems },
    dispatch,
  } = useCheckout();
  const normalizedCartItems = cartItems.map((item) => ({
    ...item,
    quantity: Number(item.quantity) ?? 1,
  }));

  const navigate = useNavigate();
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const handleCheckout = () => {
    setIsCheckoutLoading(true);

    setTimeout(() => {
      const token = uuidv4();
      navigate(`/checkout/${token}`);
      closeDrawer();
      setIsCheckoutLoading(false);
    }, 1500); // 1.5 seconds
  };

  const cartItemsRef = useRef(null);

  // Handle wheel event to prevent page scrolling when over cart
  useEffect(() => {
    const cartElement = cartItemsRef.current;

    const handleWheel = (e) => {
      if (!cartElement) return;

      // Check if the scroll is at the top or bottom of the cart
      const isScrollAtTop = cartElement.scrollTop === 0;
      const isScrollAtBottom =
        cartElement.scrollHeight - cartElement.scrollTop ===
        cartElement.clientHeight;

      // Prevent default if we're scrolling up at top or down at bottom
      if (
        (isScrollAtTop && e.deltaY < 0) ||
        (isScrollAtBottom && e.deltaY > 0)
      ) {
        e.preventDefault();
      }
    };

    if (cartElement) {
      cartElement.addEventListener("wheel", handleWheel, { passive: false });
    }

    return () => {
      if (cartElement) {
        cartElement.removeEventListener("wheel", handleWheel);
      }
    };
  }, []);

  const subtotal = normalizedCartItems.reduce((sum, item) => {
    const price = parseFloat(String(item.price).replace(/[^0-9.-]+/g, "")) || 0;
    return sum + price * item.quantity;
  }, 0);

  const handleQuantityChange = (itemId, size, change) => {
    const item = cartItems.find(
      (item) => item.id === itemId && item.size === size
    );
    if (!item) return;

    const newQuantity = (item.quantity || 1) + change;

    if (newQuantity > 0) {
      dispatch({
        type: "UPDATE_QUANTITY",
        payload: {
          ...item,
          quantity: newQuantity,
        },
      });
    } else {
      dispatch({
        type: "UPDATE_QUANTITY",
        payload: {
          ...item,
          quantity: 0,
        },
      });

      setTimeout(() => {
        dispatch({
          type: "REMOVE_FROM_CART",
          payload: { id: itemId, size: size },
        });
      }, 2000);
    }
  };

  const formatPrice = (amount, countryCode) => {
    const formatter = new Intl.NumberFormat(
      countryCode === "IN" ? "en-IN" : countryCode === "CA" ? "en-CA" : "en-US",
      {
        style: "currency",
        currency:
          countryCode === "IN" ? "INR" : countryCode === "CA" ? "CAD" : "USD",
      }
    );
    return formatter.format(amount);
  };

  return (
    <Box
      sx={{
        p: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h5" sx={{ mb: 4, mt: -1 }}>
        CART
      </Typography>

      <Divider />

      {/* Scrollable Cart Items Section */}
      <Box
        ref={cartItemsRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "0.4em",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ddd",
            borderRadius: "4px",
          },
        }}
      >
        {normalizedCartItems.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: "left", fontWeight: 500 }}>
            Your cart is currently empty.
          </Typography>
        ) : (
          normalizedCartItems.map((item, index) => {
            const itemPrice =
              parseFloat(item.price.replace(/[^0-9.-]+/g, "")) || 0;
            const itemTotal = itemPrice * (item.quantity || 1);
            const color = getColorFromTitle(item.title);

            return (
              <React.Fragment key={index}>
                <Grid
                  container
                  spacing={2}
                  alignItems="stretch"
                  sx={{ mt: 3, pb: 1, "& .MuiGrid-item": { pt: "0 !important" } }}
                >
                  <Grid item xs={3.5}>
                    <Box
                      component="img"
                      src={item.image}
                      alt={item.title}
                      sx={{
                        width: "100%",
                        borderRadius: 1,
                        aspectRatio: "1/1.4",
                        objectFit: "cover",
                      }}
                    />
                  </Grid>
                  <Grid item xs={8.5} sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <Box sx={{ mt: -0.2 }}>
                      <Typography fontWeight="500" sx={{ fontFamily: "auto", lineHeight: 1.1, mb: 0.5 }}>
                        {item.title.replace(/\|/g, "")}
                      </Typography>
                      {item.size && (
                        <Typography variant="body2" sx={{ mt: 0, fontSize: "0.85rem", color: "#666" }}>
                          Size: {item.size}
                        </Typography>
                      )}
                      <Typography fontWeight="500" sx={{ mt: 0.5 }}>
                        {formatPrice(itemTotal, userCountry?.code || "IN")}
                      </Typography>
                      {color !== "Default Color" && (
                        <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#666" }}>Colour: {color}</Typography>
                      )}
                    </Box>

                    <Grid
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.5
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          border: "1px solid #ccc",
                          width: "fit-content",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.preventDefault();
                            handleQuantityChange(item.id, item.size, -1);
                          }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ mx: 1 }}>{item.quantity}</Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.preventDefault();
                            handleQuantityChange(item.id, item.size, 1);
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </React.Fragment>
            );
          })
        )}
      </Box>

      {/* Fixed Bottom Section */}
      {normalizedCartItems.length > 0 && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "background.paper",
            pt: 2,
            pb: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="subtitle2" letterSpacing={1}>
                TOTAL
              </Typography>
            </Grid>
            <Grid item>
              <Typography>
                {formatPrice(subtotal, userCountry?.code || "IN")}
              </Typography>
            </Grid>
          </Grid>

          <Typography
            variant="subtitle1"
            sx={{ mt: 1, mb: 2, fontSize: "13.7px", color: "#666" }}
          >
            Taxes included.
          </Typography>

          <Button
            variant="contained"
            fullWidth
            disableRipple
            disableElevation
            disabled={isCheckoutLoading}
            onClick={handleCheckout}
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              borderRadius: 0,
              py: 1.5,
              mb: 1,
              fontWeight: "bold",
              minHeight: "48px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              "&:hover": {
                backgroundColor: "#000",
              },
              "&:focus": {
                backgroundColor: "#000",
              },
              "&:active": {
                backgroundColor: "#000",
              },
              "&.Mui-disabled": {
                backgroundColor: "#000",
                color: "#fff",
                opacity: 1,
              },
            }}
          >
            {isCheckoutLoading ? (
              <CircularProgress
                size={18}
                sx={{ color: "white", position: "absolute" }}
              />
            ) : null}

            <span
              style={{ visibility: isCheckoutLoading ? "hidden" : "visible" }}
            >
              CHECK OUT
            </span>
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CartDrawerContent;
